import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import BodyDiagram from "./BodyDiagram";
import SchedulePicker from "./SchedulePicker";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { HelpCircle, HeartPulse, Moon, Zap, Utensils, Trash2 } from "lucide-react";
import ProductTour from "./ProductTour";

const SIDE_EFFECT_OPTIONS = [
  { key: "nausea",   label: "Nausea",   Icon: HeartPulse },
  { key: "sleep",    label: "Sleep",    Icon: Moon },
  { key: "energy",   label: "Energy",   Icon: Zap },
  { key: "appetite", label: "Appetite", Icon: Utensils },
];

const LOG_SHOT_TOUR_KEY = "amino_log_shot_tour_seen_v1";

/**
 * Ordinal formatter — 1 → "1st", 2 → "2nd", 3 → "3rd", 4 → "4th", 21 → "21st".
 * Used for the auto-open-next toast ("You just opened your 2nd BPC-157 vial").
 */
function ordinal(n) {
  const num = Number(n) || 0;
  const abs = Math.abs(num);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  let suffix = "th";
  if (mod10 === 1 && mod100 !== 11) suffix = "st";
  else if (mod10 === 2 && mod100 !== 12) suffix = "nd";
  else if (mod10 === 3 && mod100 !== 13) suffix = "rd";
  return `${num}${suffix}`;
}
const LOG_SHOT_TOUR_STEPS = [
  {
    placement: "center",
    title: "How to log a shot",
    body: "60-second walkthrough of every field. Use ← → to move, Esc to skip. Ready?",
  },
  {
    selector: '[data-testid="shot-peptide-select"]',
    placement: "bottom",
    title: "1. Pick your peptide",
    body: "Search the library or scroll to select. Defaults for dose amount and injection site auto-fill based on your chosen peptide.",
  },
  {
    selector: '[data-testid="shot-dose-amount"]',
    placement: "bottom",
    title: "2. Dose amount + unit",
    body: "Enter the amount, then pick the unit next to it (mcg / mg / IU / mL). If you use the Calculator first, it'll auto-fill this here.",
  },
  {
    selector: '[data-testid="shot-taken-at"]',
    placement: "bottom",
    title: "3. When you took it",
    body: "Defaults to right now. Backfill an older shot by picking a past time — the countdown recalculates automatically.",
  },
  {
    selector: '[data-testid="shot-interval-wrapper"]',
    placement: "bottom",
    title: "4. When's your next dose?",
    body: "Pick 'Hours' for sub-daily protocols, 'Days' for every-N-days (weekly = 7, biweekly = 14), or 'Weekday' to hit specific days like Mon/Wed/Fri. Drives the Dashboard countdown.",
  },
  {
    selector: '[data-testid="shot-site"]',
    placement: "top",
    title: "5. Injection site",
    body: "Tap the body diagram — green dots are rested spots, red dots are recently used. A REC chip marks the site that's rested the longest, or type a custom site here.",
  },
  {
    selector: '[data-testid="shot-notes"]',
    placement: "top",
    title: "6. Notes (optional)",
    body: "Symptoms, sensations, batch info — anything you want to look back on later. Notes are searchable in History.",
  },
  {
    selector: '[data-testid="shot-side-effects"]',
    placement: "top",
    title: "7. How you're feeling",
    body: "One-tap tags — Nausea, Sleep, Energy, Appetite. Add any that apply and you'll spot patterns over time in your History.",
  },
  {
    selector: '[data-testid="shot-save"]',
    placement: "top",
    title: "8. Save",
    body: "Locks in the shot, deducts from the matching vial (FIFO), restarts your countdown, and drops the entry into History. Done!",
  },
];

function toLocalInput(date) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function LogShotDialog({ open, onOpenChange, peptides = [], initial, initialPeptide, onSaved, shots = [] }) {
  const isEdit = !!initial;
  const [peptideName, setPeptideName] = useState("");
  const [doseAmount, setDoseAmount] = useState("");
  const [doseUnit, setDoseUnit] = useState("mcg");
  const [injectionSite, setInjectionSite] = useState("");
  const [notes, setNotes] = useState("");
  const [takenAt, setTakenAt] = useState(toLocalInput(new Date()));
  const [intervalHours, setIntervalHours] = useState("24");
  const [schedule, setSchedule] = useState({ type: "hours", value: 24, weekdays: null });
  const [customPeptideMode, setCustomPeptideMode] = useState(false);
  const [noFollowup, setNoFollowup] = useState(false);
  const [sideEffects, setSideEffects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  // First-open auto-launch of the log-shot mini-tour (only when creating, not editing)
  useEffect(() => {
    if (!open || isEdit) return;
    try {
      if (!localStorage.getItem(LOG_SHOT_TOUR_KEY)) {
        const t = setTimeout(() => setTourOpen(true), 500);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, [open, isEdit]);

  const closeTour = () => {
    setTourOpen(false);
    try { localStorage.setItem(LOG_SHOT_TOUR_KEY, "1"); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setPeptideName(initial.peptide_name || "");
      // Auto-open custom mode when editing a shot whose peptide is not in the library.
      const libraryNames = new Set(peptides.map((p) => p.name));
      setCustomPeptideMode(!!initial.peptide_name && !libraryNames.has(initial.peptide_name));
      setDoseAmount(String(initial.dose_amount ?? ""));
      setDoseUnit(initial.dose_unit || "mcg");
      setInjectionSite(initial.injection_site || "");
      setNotes(initial.notes || "");
      setTakenAt(toLocalInput(initial.taken_at));
      setIntervalHours(String(initial.interval_hours ?? "24"));
      const s = initial.schedule;
      setSchedule(
        s && s.type
          ? { type: s.type, value: s.value ?? null, weekdays: s.weekdays ?? null }
          : { type: "hours", value: Number(initial.interval_hours) || 24, weekdays: null }
      );
      setSideEffects(Array.isArray(initial.side_effects) ? initial.side_effects : []);
      setNoFollowup(!!initial.no_followup);
    } else {
      const name = initialPeptide || "";
      setPeptideName(name);
      setCustomPeptideMode(false);
      setInjectionSite("");
      setNotes("");
      setTakenAt(toLocalInput(new Date()));
      setSideEffects([]);
      setNoFollowup(false);
      const p = name ? peptides.find((x) => x.name === name) : null;
      if (p) {
        setDoseAmount(p.default_dose != null ? String(p.default_dose) : "");
        setDoseUnit(p.default_unit || "mcg");
        setIntervalHours(String(p.default_interval_hours || "24"));
        setSchedule({ type: "hours", value: Number(p.default_interval_hours) || 24, weekdays: null });
      } else {
        setDoseAmount("");
        setDoseUnit("mcg");
        setIntervalHours("24");
        setSchedule({ type: "hours", value: 24, weekdays: null });
      }
    }
  }, [open, initial, initialPeptide, peptides]);

  const handlePeptideSelect = (name) => {
    setPeptideName(name);
    const p = peptides.find((x) => x.name === name);
    if (p && !isEdit) {
      if (p.default_dose != null) setDoseAmount(String(p.default_dose));
      if (p.default_unit) setDoseUnit(p.default_unit);
      if (p.default_interval_hours) {
        setIntervalHours(String(p.default_interval_hours));
        setSchedule({ type: "hours", value: Number(p.default_interval_hours), weekdays: null });
      }
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!peptideName) {
      toast.error("Choose a peptide");
      return;
    }
    // Client-side sanity: weekdays mode needs at least one day picked.
    if (schedule.type === "weekdays" && (!schedule.weekdays || schedule.weekdays.length === 0)) {
      toast.error("Pick at least one weekday");
      return;
    }
    // Derive a fallback interval_hours so the API stays happy (server will
    // recompute the authoritative value from the schedule).
    const fallbackHours =
      schedule.type === "hours"
        ? Number(schedule.value) || 24
        : schedule.type === "days"
        ? (Number(schedule.value) || 1) * 24
        : 168; // weekdays → weekly-ish placeholder; server resolves the real value
    setSubmitting(true);
    try {
      const payload = {
        peptide_name: peptideName,
        dose_amount: parseFloat(doseAmount),
        dose_unit: doseUnit,
        injection_site: injectionSite || null,
        notes: notes || null,
        taken_at: new Date(takenAt).toISOString(),
        interval_hours: fallbackHours,
        schedule: {
          type: schedule.type,
          value: schedule.type === "weekdays" ? null : (Number(schedule.value) || null),
          weekdays: schedule.type === "weekdays" ? (schedule.weekdays || []) : null,
        },
        side_effects: sideEffects,
        no_followup: noFollowup,
      };
      if (isEdit) {
        await api.put(`/shots/${initial.id}`, payload);
        toast.success("Shot updated");
      } else {
        const res = await api.post("/shots", payload);
        const d = res.data || {};
        if (d.deducted_vial_id && d.deducted_vial_peptide_name != null) {
          const rem = Number(d.deducted_vial_remaining_mg ?? 0);
          const total = Number(d.deducted_vial_amount_mg ?? 0);
          const label = total > 0 ? `${rem.toFixed(2)} / ${total.toFixed(2)} mg left` : `${rem.toFixed(2)} mg left`;
          if (d.deducted_vial_auto_opened_next) {
            // Backend auto-cracked open a sealed vial. Celebrate with an ordinal
            // callout ("your 2nd BPC-157 vial") and remind them how many sealed spares remain.
            const sealedLeft = Math.max(0, Number(d.deducted_vial_quantity ?? 1) - 1);
            const openedIndex = ordinal(Number(d.deducted_vial_opens_count) || 2);
            toast.success(
              `You just opened your ${openedIndex} ${d.deducted_vial_peptide_name} vial`,
              {
                description: sealedLeft === 0
                  ? "No sealed vials left — order a restock when you can."
                  : `${sealedLeft} sealed left · ${label} in this one.`,
                duration: 6500,
              }
            );
          } else {
            toast.success(
              `Shot logged • ${d.deducted_vial_peptide_name} vial → ${label}`,
              { description: rem <= 0 ? "Vial is empty — restock soon." : rem < total * 0.25 ? "Vial running low." : undefined, duration: 6000 }
            );
          }
        } else {
          toast.success("Shot logged", { description: "No matching vial in inventory to deduct.", duration: 4500 });
        }
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="log-shot-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="font-display uppercase tracking-tight text-3xl font-black">
                {isEdit ? "Edit Shot" : "Log New Shot"}
              </DialogTitle>
              <DialogDescription className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-500 mt-1">
                // {isEdit ? "modify existing entry" : "record dose + timestamp"}
              </DialogDescription>
            </div>
            {!isEdit && (
              <button
                type="button"
                onClick={() => setTourOpen(true)}
                title="How to log a shot"
                aria-label="How to log a shot"
                data-testid="log-shot-tour-trigger"
                className="mt-1 mr-6 shrink-0 w-8 h-8 flex items-center justify-center border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-500 transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
            )}
          </div>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="log-shot-form">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Peptide</Label>
            {customPeptideMode ? (
              <div className="space-y-1.5" data-testid="shot-peptide-custom-wrapper">
                <Input
                  autoFocus
                  value={peptideName}
                  onChange={(e) => setPeptideName(e.target.value)}
                  placeholder="Type your peptide name (e.g. Kisspeptin-10)"
                  maxLength={120}
                  className="bg-slate-50 border-slate-200 rounded-none h-11"
                  data-testid="shot-peptide-custom-input"
                />
                <button
                  type="button"
                  onClick={() => { setCustomPeptideMode(false); setPeptideName(""); }}
                  className="text-[11px] uppercase tracking-[0.2em] text-teal-700 hover:text-teal-800 font-mono-num"
                  data-testid="shot-peptide-back-to-library"
                >
                  ← Pick from library instead
                </button>
              </div>
            ) : (
              <Select
                value={peptideName}
                onValueChange={(v) => {
                  if (v === "__custom__") { setCustomPeptideMode(true); setPeptideName(""); }
                  else handlePeptideSelect(v);
                }}
              >
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="shot-peptide-select">
                  <SelectValue placeholder="Select peptide" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none max-h-[300px]">
                  <SelectItem value="__custom__" data-testid="peptide-option-custom">
                    <div className="flex items-center gap-2 text-teal-700 font-semibold">
                      <span>+ Custom peptide…</span>
                      <span className="text-[10px] text-slate-500 uppercase tracking-widest">not on the list</span>
                    </div>
                  </SelectItem>
                  {peptides.map((p) => (
                    <SelectItem key={p.id} value={p.name} data-testid={`peptide-option-${p.name}`}>
                      <div className="flex items-center gap-2">
                        <span>{p.name}</span>
                        {p.category && <span className="text-[10px] text-slate-500 uppercase tracking-widest">{p.category}</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Dose Amount</Label>
              <Input type="number" step="any" min="0" required value={doseAmount} onChange={(e) => setDoseAmount(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="shot-dose-amount" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Unit</Label>
              <Select value={doseUnit} onValueChange={setDoseUnit}>
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="shot-dose-unit"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none">
                  <SelectItem value="mcg">mcg</SelectItem>
                  <SelectItem value="mg">mg</SelectItem>
                  <SelectItem value="IU">IU</SelectItem>
                  <SelectItem value="mL">mL</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Taken At</Label>
            <Input type="datetime-local" required value={takenAt} onChange={(e) => setTakenAt(e.target.value)}
              className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="shot-taken-at" />
          </div>

          <div className="space-y-1.5" data-testid="shot-interval-wrapper">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">
              Cadence <span className="text-slate-400 normal-case tracking-normal">— how often you take it</span>
            </Label>
            <SchedulePicker value={schedule} onChange={setSchedule} testIdPrefix="shot" />
          </div>

          <label
            className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
              noFollowup ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20" : "border-slate-200 hover:border-amber-300"
            }`}
            data-testid="shot-no-followup-wrapper"
          >
            <input
              type="checkbox"
              checked={noFollowup}
              onChange={(e) => setNoFollowup(e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-amber-500 shrink-0"
              data-testid="shot-no-followup-checkbox"
            />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Do not schedule another dose
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug mt-0.5">
                One-time entry — the shot is logged and the vial is deducted, but no countdown or reminder will be created for this peptide.
              </div>
            </div>
          </label>

          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Injection Site</Label>
            <BodyDiagram value={injectionSite} onChange={setInjectionSite} shots={shots} />
            <Input value={injectionSite} onChange={(e) => setInjectionSite(e.target.value)}
              placeholder="Tap the body diagram or type a custom site"
              className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="shot-site" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Optional notes"
              className="bg-slate-50 border-slate-200 rounded-none resize-none" data-testid="shot-notes" />
          </div>

          <div className="space-y-2" data-testid="shot-side-effects">
            <div className="flex items-baseline justify-between">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">
                How I'm feeling
              </Label>
              <span className="text-[10px] text-slate-400 uppercase tracking-widest">Tap to toggle</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {SIDE_EFFECT_OPTIONS.map(({ key, label, Icon }) => {
                const active = sideEffects.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() =>
                      setSideEffects((prev) =>
                        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
                      )
                    }
                    aria-pressed={active}
                    data-testid={`side-effect-toggle-${key}`}
                    className={[
                      "flex flex-col items-center gap-1 py-3 px-2 border transition-colors touch-manipulation select-none",
                      active
                        ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]"
                        : "border-slate-200 bg-slate-50 text-slate-500 hover:border-slate-300 hover:text-slate-800",
                    ].join(" ")}
                  >
                    <Icon className="w-4 h-4" strokeWidth={2} />
                    <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <DialogFooter className="pt-2">
            {isEdit && (
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  // Snapshot every field so Undo can rebuild the exact shot.
                  const snap = {
                    peptide_name: initial.peptide_name,
                    dose_amount: initial.dose_amount,
                    dose_unit: initial.dose_unit,
                    injection_site: initial.injection_site || null,
                    notes: initial.notes || null,
                    taken_at: initial.taken_at,
                    interval_hours: initial.interval_hours || 24,
                    schedule: initial.schedule || null,
                    side_effects: initial.side_effects || [],
                  };
                  try {
                    await api.delete(`/shots/${initial.id}`);
                    onSaved?.();
                    onOpenChange(false);
                    toast(`Deleted shot: ${snap.peptide_name} · ${snap.dose_amount}${snap.dose_unit}`, {
                      duration: 10000,
                      action: {
                        label: "Undo",
                        onClick: async () => {
                          try {
                            await api.post("/shots", snap);
                            onSaved?.();
                            toast.success("Restored");
                          } catch (err) { toast.error(formatApiError(err)); }
                        },
                      },
                    });
                  } catch (err) { toast.error(formatApiError(err)); }
                }}
                className="border-red-300 bg-white hover:bg-red-50 text-red-700 rounded-none uppercase tracking-widest font-semibold mr-auto"
                data-testid="shot-delete"
                title="Delete this shot (undo available for 10 seconds)"
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800" data-testid="shot-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}
              className="bg-[#0D9488] hover:bg-[#0F766E] rounded-none text-white uppercase tracking-widest font-semibold"
              data-testid="shot-save">
              {submitting ? "Saving…" : (isEdit ? "Update" : "Log Shot")}
            </Button>
          </DialogFooter>
        </form>
        <ProductTour steps={LOG_SHOT_TOUR_STEPS} open={tourOpen} onClose={closeTour} />
      </DialogContent>
    </Dialog>
  );
}
